import { useEffect, useRef, useState } from 'react';
import { Headphones } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlanBlock from '../components/profile/PlanBlock';
import CreditsBlock from '../components/profile/CreditsBlock';
import ReferralBlock from '../components/profile/ReferralBlock';
import LegalDocumentsSheet from '../components/profile/LegalDocumentsSheet';
import BottomSheet from '../components/BottomSheet';
import LegalDocumentViewer from '../components/legal/LegalDocumentViewer';
import { useAuthStore } from '../store/authStore';
import { useConsentStore } from '../store/consentStore';
import {
  useProfileQuery,
  useReferralQuery,
  useSubscriptionCurrentQuery,
  useUpdateProfileLanguageMutation,
} from '../core/queries/account';
import { resolveActivePlanKey, resolveCreditsTotal } from '../core/accountPresentation';
import { hapticSelection } from '../utils/haptics';
import useConsentGuard from '../hooks/useConsentGuard';
import {
  normalizeLanguageCode,
  SUPPORTED_LOCALES,
} from '../locales/langConfig';

const SUPPORT_BOT_USERNAME = String(
  import.meta.env.VITE_SUPPORT_BOT_USERNAME || 'IMGA_24bot',
).replace(/^@+/, '');

/**
 * ProfilePage — /profile (Figma 318:5922, redesign 2026-05-21, Stage 08.3).
 *
 * Открывается ТОЛЬКО через tap на BalancePlanChip на Home.
 * Нет вкладки в bottom-nav.
 * Нет AppLayout (нет BottomNav) — маршрут вне <AppLayout>.
 *
 * Figma nodes (3 tier variants — only the inner plan card differs):
 *   - Free    → 318:5922 (plan card 318:5925, variant `free`)
 *   - Premium → 318:5929 (plan card 318:5932, variant `premium`)
 *   - Ultra   → 345:4225 (plan card 318:5939, variant `ultra`)
 * Balance + referral cards are identical across all three frames.
 *
 * Layout (per Figma body 318:5924):
 *   padding: 16/16/0/16 (no bottom — BottomNav owns its own padding)
 *   itemSpacing: 16 between cards
 *
 * Структура (handoff §13):
 *  - Сквозная `PersistentBalanceChip` from App shell overlays the page top
 *  - PlanBlock    — текущий план, дата окончания, «Сменить план» → /plans
 *  - CreditsBlock — остаток кредитов, «Купить кредиты» → /credits,
 *                   «История» → /history
 *  - ReferralBlock — ссылка (copy + share), счётчик друзей
 *
 * Данные:
 *   GET /api/user/profile  → { user } (backend routes/user.js:462)
 *   GET /api/referral/info → { referralLink, totalReferrals, ... }
 * Auth: X-Telegram-Id (добавляется interceptor-ом в api/client.js)
 *
 * Edge cases:
 *  - 401 (нет telegramId) → navigate('/')
 *  - /api/referral/info ошибка → ReferralBlock без ссылки
 *  - 0 кредитов → показываем «0», блок не скрываем
 */

export default function ProfilePage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const versionChanged = useConsentStore((state) => state.versionChanged);
  const hasConsentHistory = useConsentStore((state) => state.hasConsentHistory);
  const welcomeBonusCredits = useConsentStore((state) => state.welcomeBonusCredits);
  const authUser = useAuthStore((state) => state.user);
  const authCredits = useAuthStore((state) => state.credits);
  const authReferral = useAuthStore((state) => state.referral);
  const { requireConsent } = useConsentGuard();
  const isWelcomePromo = (
    consentRequired === true
    && versionChanged !== true
    && hasConsentHistory !== true
  );
  const canDisplayRealAccount = consentRequired === false || versionChanged === true;
  const accountQueriesEnabled = consentRequired === false;
  const profileQuery = useProfileQuery({ enabled: accountQueriesEnabled });
  const referralQuery = useReferralQuery({
    retry: false,
    enabled: accountQueriesEnabled,
  });
  const subscriptionQuery = useSubscriptionCurrentQuery({
    retry: false,
    enabled: accountQueriesEnabled,
  });
  const updateLanguageMutation = useUpdateProfileLanguageMutation();
  const authUserId = useAuthStore((state) => state.user?.id);
  const setUserLanguage = useAuthStore((state) => state.setUserLanguage);
  const [languageSheetOpen, setLanguageSheetOpen] = useState(false);
  const [legalSheetOpen, setLegalSheetOpen] = useState(false);
  const [legalDocumentSlug, setLegalDocumentSlug] = useState(null);
  const languageSubmitRef = useRef(false);

  useEffect(() => {
    if (profileQuery.error?.response?.status === 401) navigate('/');
  }, [navigate, profileQuery.error]);

  const profile = isWelcomePromo
    ? {
        id: authUser?.id,
        language: authUser?.language,
        plan: 'FREE',
        creditsMonthly: welcomeBonusCredits,
        creditsExtra: 0,
      }
    : profileQuery.data?.user || profileQuery.data || authUser || null;
  const referralUrl =
    referralQuery.data?.referralLink ||
    referralQuery.data?.url ||
    referralQuery.data?.link ||
    authReferral?.referralLink ||
    null;
  const referralCount =
    referralQuery.data?.totalReferrals ??
    authReferral?.totalReferrals ??
    0;
  const subscription = subscriptionQuery.data?.subscription ?? null;

  if (accountQueriesEnabled && profileQuery.isLoading) {
    return (
      <div
        className="min-h-screen bg-bg-app flex items-center justify-center"
        aria-label={t('profile.loadingLabel')}
      >
        <span className="font-sans text-plan text-text-secondary">{t('common.loading')}</span>
      </div>
    );
  }

  if (accountQueriesEnabled && profileQuery.isError) {
    return (
      <div
        className="min-h-screen bg-bg-app flex items-center justify-center px-body-x"
        role="alert"
      >
        <p className="font-sans text-plan text-text-secondary text-center">
          {t('profile.loadError')}
        </p>
      </div>
    );
  }

  // Normalise profile data — backend returns flat User row (no nested .subscription)
  // User fields: plan (FREE|PREMIUM|ULTRA), creditsMonthly, creditsExtra
  const u = profile || {};
  const planKey = resolveActivePlanKey({ subscription, user: u });
  // expiresAt from active subscription (Task 1.2: /subscription/current).
  // Falls back to flat user-row field for compat if backend ever exposes it.
  const expiresAt = subscription?.expiresAt || u.planExpiresAt || null;

  // Credits: flat fields creditsMonthly + creditsExtra (backend User row)
  const credits = isWelcomePromo
    ? welcomeBonusCredits
    : resolveCreditsTotal({
        credits: profileQuery.data ? null : authCredits,
        user: u,
      });
  const activeLanguage = normalizeLanguageCode(
    i18n.resolvedLanguage || i18n.language,
  );
  const activeLocale =
    SUPPORTED_LOCALES.find(({ code }) => code === activeLanguage) ||
    SUPPORTED_LOCALES[0];

  const handleSupportClick = () => {
    hapticSelection();
    const userId = authUserId || u.id;
    const startParam = userId ? `?start=profile_${encodeURIComponent(userId)}` : '';
    const url = `https://t.me/${SUPPORT_BOT_USERNAME}${startParam}`;
    const tg = window.Telegram?.WebApp;
    if (typeof tg?.openTelegramLink === 'function') {
      try {
        tg.openTelegramLink(url);
        return;
      } catch {
        /* fall through to the browser-safe link */
      }
    }
    window.open(url, '_blank', 'noopener');
  };

  const handleLanguageSelect = async (language) => {
    if (languageSubmitRef.current || updateLanguageMutation.isPending) return;
    if (language === activeLanguage) {
      setLanguageSheetOpen(false);
      return;
    }

    languageSubmitRef.current = true;
    const previousLanguage = activeLanguage;
    hapticSelection();

    try {
      await i18n.changeLanguage(language);
      const result = await updateLanguageMutation.mutateAsync(language);
      setUserLanguage(result.user.language);
      setLanguageSheetOpen(false);
    } catch {
      await i18n.changeLanguage(previousLanguage);
    } finally {
      languageSubmitRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-bg-app text-text-primary">
      {/* Preserve the existing safe-area composition outside iOS while applying
          the shared iOS page-content shift to the profile cards. */}
      <div
        className="flex flex-col gap-4 px-body-x pb-4"
        style={{
          paddingTop:
            'calc(var(--safe-top, 0px) + 112px - var(--page-content-top-shift, 0px))',
        }}
        data-testid="profile-content"
      >
        {isWelcomePromo || canDisplayRealAccount ? (
          <PlanBlock
            plan={planKey}
            expiresAt={expiresAt}
            onChangePlan={() => requireConsent(() => navigate('/plans'), 'purchase')}
          />
        ) : null}

        <CreditsBlock
          credits={credits}
          onBuyCredits={() => navigate('/credits')}
          onHistory={() => navigate('/history')}
        />

        <ReferralBlock
          referralUrl={referralUrl}
          referralCount={referralCount}
          restricted={consentRequired !== false}
        />

        <div
          className="mt-[15px] flex w-full flex-col items-center"
          data-testid="profile-footer-actions"
        >
          <div
            className="flex flex-nowrap items-center justify-center gap-2"
            data-testid="profile-action-buttons"
          >
            <button
              type="button"
              onClick={handleSupportClick}
              className={[
                'inline-flex items-center gap-[7.5px]',
                'rounded-home-20 px-[7.5px] py-[3.75px]',
                'bg-gradient-to-l from-glass-24-start to-glass-24-end',
                'whitespace-nowrap font-sans font-medium text-[15px] leading-[17.61px] text-white',
                'backdrop-blur-glass-strong transition-transform active:scale-[0.97]',
              ].join(' ')}
              aria-label={t('profile.support')}
              data-testid="profile-support-button"
            >
              <Headphones aria-hidden="true" size={22} strokeWidth={2.4} />
              <span>{t('profile.support')}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                hapticSelection();
                setLanguageSheetOpen(true);
              }}
              className={[
                'inline-flex items-center gap-[7.5px]',
                'rounded-home-20 px-[7.5px] py-[3.75px]',
                'bg-gradient-to-l from-glass-24-start to-glass-24-end',
                'whitespace-nowrap font-sans font-medium text-[15px] leading-[17.61px] text-white',
                'backdrop-blur-glass-strong transition-transform active:scale-[0.97]',
              ].join(' ')}
              aria-label={t('profile.language.title')}
              data-testid="profile-language-button"
            >
              <span aria-hidden="true" className="text-[18px] leading-[22px]">
                {activeLocale.flag}
              </span>
              <span>{activeLocale.shortCode}</span>
            </button>
          </div>

          <a
            href="#legal-documents"
            onClick={(event) => {
              event.preventDefault();
              hapticSelection();
              setLegalSheetOpen(true);
            }}
            className={[
              'mt-4 font-sans font-bold text-cta-sm text-text-secondary',
              'underline decoration-current underline-offset-2',
              'transition-opacity hover:opacity-90 active:opacity-70',
            ].join(' ')}
            data-testid="profile-legal-documents-button"
          >
            {t('profile.legalDocuments.button')}
          </a>
        </div>
      </div>

      <BottomSheet
        open={languageSheetOpen}
        busy={updateLanguageMutation.isPending}
        title={t('profile.language.title')}
        onClose={() => setLanguageSheetOpen(false)}
        testId="language-picker-sheet"
      >
        <div className="flex flex-col gap-2">
          {SUPPORTED_LOCALES.map((locale) => {
            const selected = locale.code === activeLanguage;
            return (
              <button
                key={locale.code}
                type="button"
                onClick={() => handleLanguageSelect(locale.code)}
                disabled={updateLanguageMutation.isPending}
                aria-pressed={selected}
                className={[
                  'flex min-h-14 w-full items-center gap-3 rounded-2xl px-4 py-3 text-left',
                  'border font-sans transition-colors',
                  selected
                    ? 'border-[#FFAC0B]/60 bg-[#FFAC0B]/10'
                    : 'border-white/[0.08] bg-white/[0.06]',
                  updateLanguageMutation.isPending ? 'opacity-60' : '',
                ].join(' ')}
                data-testid={`language-option-${locale.code}`}
              >
                <span aria-hidden="true" className="text-2xl leading-none">
                  {locale.flag}
                </span>
                <span className="min-w-0 flex-1 whitespace-nowrap text-[16px] font-semibold text-text-primary">
                  {locale.nativeName}
                </span>
                <span
                  aria-hidden="true"
                  className={[
                    'w-5 text-right text-lg font-bold',
                    selected ? 'text-[#FFAC0B]' : 'text-transparent',
                  ].join(' ')}
                >
                  ✓
                </span>
              </button>
            );
          })}
        </div>
      </BottomSheet>

      <LegalDocumentsSheet
        open={legalSheetOpen}
        onClose={() => setLegalSheetOpen(false)}
        onSelect={(slug) => {
          setLegalSheetOpen(false);
          setLegalDocumentSlug(slug);
        }}
      />

      {legalDocumentSlug && (
        <LegalDocumentViewer
          slug={legalDocumentSlug}
          locale={i18n.resolvedLanguage || i18n.language || 'en'}
          onClose={() => {
            setLegalDocumentSlug(null);
            setLegalSheetOpen(true);
          }}
        />
      )}
    </div>
  );
}
